/**
 * A small canvas line/area chart.
 *
 * Canvas rather than SVG because several of these are also uploaded as textures
 * onto the tool HMI screens in the 3D scene, and a canvas is the one surface
 * both consumers can share. The interaction layer is still here: a crosshair
 * and value readout follow the pointer whenever the chart is in the DOM panel,
 * because a line chart a student cannot interrogate is a picture, not an
 * instrument.
 */

import { sci } from '../core/sci'
import { FONT_STACK, INK, SERIES, SURFACE } from './palette'
import { clamp } from '../core/math'

export interface ChartSeries {
  label: string
  points: Array<[number, number]>
  /** Defaults to the next categorical slot, in fixed order. */
  colour?: string
  /** Fill under the line - used for distributions, not for comparisons. */
  fill?: boolean
  dashed?: boolean
  /**
   * Draw a dot at every point. For measurements rather than curves - a
   * handful of runs on an Arrhenius plot - where the line between them
   * would claim a continuity the data does not have. Pair with
   * `noLine` for a plain scatter.
   */
  markers?: boolean
  /** Skip the polyline; only meaningful with markers. */
  noLine?: boolean
}

export interface ChartAnnotation {
  /** Draw a vertical rule at this x. */
  x?: number
  /** Draw a horizontal rule at this y. */
  y?: number
  label: string
  colour?: string
}

export interface ChartOptions {
  title?: string
  xLabel?: string
  yLabel?: string
  series: ChartSeries[]
  xMin?: number
  xMax?: number
  yMin?: number
  yMax?: number
  annotations?: ChartAnnotation[]
  /** Formatter for the hover readout. */
  format?: (x: number, y: number) => string
}

const PAD = { left: 46, right: 12, top: 24, bottom: 30 }

export class Chart {
  readonly canvas: HTMLCanvasElement
  private readonly ctx: CanvasRenderingContext2D
  /**
   * The size the chart was ASKED for, in CSS pixels.
   *
   * This is the fallback drawing box for a chart that is never inserted
   * into the DOM - an HMI texture in the 3D scene has no client width and
   * has to lay out somewhere. A chart on a page lays out at its real
   * displayed size instead: see `box()`.
   */
  private readonly askedW: number
  private readonly askedH: number
  private last: ChartOptions | null = null
  private hover: { x: number; y: number } | null = null
  /** Backing store per CSS pixel; 1 until the element has been measured. */
  private dpr = 1

  constructor(width = 360, height = 190) {
    this.askedW = width
    this.askedH = height
    this.canvas = document.createElement('canvas')
    this.canvas.width = width
    this.canvas.height = height
    this.canvas.style.width = '100%'
    this.canvas.style.display = 'block'
    this.ctx = this.canvas.getContext('2d')!
    this.attachHover()
    this.attachResize()
  }

  /**
   * THE DRAWING BOX, in CSS pixels: the element's own displayed size once
   * it is on a page, and the constructed size while it is not.
   *
   * Everything render() draws - type, padding, line weights, tick spacing
   * - is in these units, so it comes out the same size on screen whatever
   * width the container gives the chart. A wider chart shows more plot
   * rather than a bigger picture of the same plot.
   *
   * It did not always work that way, and the symptom is worth recording.
   * The chart used to lay out in a FIXED box the constructor named, 430
   * units wide by default, and scale that box to fill the container. On a
   * full-screen browser the furnace panel put one chart in an 830 px
   * column and a pair in 358 px columns, so the same page drew its type at
   * 193% in one chart and 83% in the next, a factor of 2.3 apart. The wide
   * ones read as cartoons.
   */
  private box(): { w: number; h: number } {
    const w = this.canvas.clientWidth
    const h = this.canvas.clientHeight
    return w > 0 && h > 0 ? { w, h } : { w: this.askedW, h: this.askedH }
  }

  private observer: ResizeObserver | null = null

  /** Stop watching layout. For charts owned by unmounting UI, not textures. */
  dispose(): void {
    this.observer?.disconnect()
    this.observer = null
  }

  private attachResize(): void {
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const { w: cssW, h: cssH } = this.box()
      if (!cssW || !cssH) return
      // The backing store follows BOTH displayed dimensions. Deriving the
      // height from the width and a fixed aspect, as this once did, left a
      // chart whose height had been pinned drawing a 129 px bitmap into a
      // 155 px box and stretching it by a fifth.
      this.dpr = Math.min(window.devicePixelRatio || 1, 3)
      const w = Math.round(cssW * this.dpr)
      const h = Math.round(cssH * this.dpr)
      if (w === this.canvas.width && h === this.canvas.height) return
      this.canvas.width = w
      this.canvas.height = h
      if (this.last) this.render(this.last)
    })
    ro.observe(this.canvas)
    this.observer = ro
  }

  private attachHover(): void {
    this.canvas.addEventListener('mousemove', (e) => {
      // The drawing box is now CSS pixels, so a pointer offset needs no
      // scaling at all.
      const rect = this.canvas.getBoundingClientRect()
      this.hover = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      if (this.last) this.render(this.last)
    })
    this.canvas.addEventListener('mouseleave', () => {
      this.hover = null
      if (this.last) this.render(this.last)
    })
  }

  render(opts: ChartOptions): HTMLCanvasElement {
    this.last = opts
    const ctx = this.ctx
    // One CSS pixel is one drawing unit; the device ratio is the only
    // scaling, and it buys sharpness rather than size.
    const { w: W, h: H } = this.box()
    const scale = this.canvas.width / W
    ctx.setTransform(scale, 0, 0, scale, 0, 0)

    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = SURFACE
    ctx.fillRect(0, 0, W, H)

    const plot = {
      x: PAD.left,
      y: PAD.top,
      w: W - PAD.left - PAD.right,
      h: H - PAD.top - PAD.bottom,
    }

    // --- domain ---
    let xMin = opts.xMin ?? Infinity
    let xMax = opts.xMax ?? -Infinity
    let yMin = opts.yMin ?? Infinity
    let yMax = opts.yMax ?? -Infinity
    for (const s of opts.series) {
      for (const [x, y] of s.points) {
        if (opts.xMin === undefined) xMin = Math.min(xMin, x)
        if (opts.xMax === undefined) xMax = Math.max(xMax, x)
        if (opts.yMin === undefined) yMin = Math.min(yMin, y)
        if (opts.yMax === undefined) yMax = Math.max(yMax, y)
      }
    }
    if (!Number.isFinite(xMin) || xMin === xMax) {
      xMin = 0
      xMax = 1
    }
    if (!Number.isFinite(yMin) || yMin === yMax) {
      yMin = 0
      yMax = 1
    }
    // A little headroom so the top series is not welded to the frame.
    if (opts.yMax === undefined) yMax += (yMax - yMin) * 0.12

    const sx = (x: number) => plot.x + ((x - xMin) / (xMax - xMin)) * plot.w
    const sy = (y: number) => plot.y + plot.h - ((y - yMin) / (yMax - yMin)) * plot.h

    // --- recessive grid and axis ---
    ctx.strokeStyle = INK.grid
    ctx.lineWidth = 1
    ctx.font = `10px ${FONT_STACK}`
    ctx.fillStyle = INK.muted
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'

    const yTicks = 4
    for (let i = 0; i <= yTicks; i++) {
      const v = yMin + ((yMax - yMin) * i) / yTicks
      const py = Math.round(sy(v)) + 0.5
      ctx.beginPath()
      ctx.moveTo(plot.x, py)
      ctx.lineTo(plot.x + plot.w, py)
      ctx.stroke()
      const mag = Math.abs(v)
      ctx.fillText(mag >= 1000 ? sci(v) : mag >= 10 ? v.toFixed(0) : v.toFixed(2), plot.x - 6, py)
    }

    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    const xTicks = 4
    for (let i = 0; i <= xTicks; i++) {
      const v = xMin + ((xMax - xMin) * i) / xTicks
      const px = Math.round(sx(v)) + 0.5
      ctx.fillText(Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2), px, plot.y + plot.h + 6)
    }

    ctx.strokeStyle = INK.axis
    ctx.beginPath()
    ctx.moveTo(plot.x, plot.y + plot.h + 0.5)
    ctx.lineTo(plot.x + plot.w, plot.y + plot.h + 0.5)
    ctx.stroke()

    // --- annotations sit under the data ---
    for (const a of opts.annotations ?? []) {
      ctx.save()
      ctx.strokeStyle = a.colour ?? INK.muted
      ctx.setLineDash([3, 3])
      ctx.lineWidth = 1
      ctx.beginPath()
      if (a.x !== undefined) {
        const px = Math.round(sx(a.x)) + 0.5
        ctx.moveTo(px, plot.y)
        ctx.lineTo(px, plot.y + plot.h)
      } else if (a.y !== undefined) {
        const py = Math.round(sy(a.y)) + 0.5
        ctx.moveTo(plot.x, py)
        ctx.lineTo(plot.x + plot.w, py)
      }
      ctx.stroke()
      ctx.restore()

      ctx.fillStyle = a.colour ?? INK.muted
      ctx.font = `9px ${FONT_STACK}`
      ctx.textAlign = a.x !== undefined ? 'left' : 'right'
      ctx.textBaseline = 'top'
      if (a.x !== undefined) ctx.fillText(a.label, sx(a.x) + 4, plot.y + 2)
      else if (a.y !== undefined) ctx.fillText(a.label, plot.x + plot.w - 4, sy(a.y) + 3)
    }

    // --- series ---
    opts.series.forEach((s, i) => {
      const colour = s.colour ?? SERIES[i % SERIES.length]
      if (s.points.length === 0) return

      if (s.fill) {
        ctx.beginPath()
        ctx.moveTo(sx(s.points[0][0]), plot.y + plot.h)
        for (const [x, y] of s.points) ctx.lineTo(sx(x), sy(y))
        ctx.lineTo(sx(s.points[s.points.length - 1][0]), plot.y + plot.h)
        ctx.closePath()
        ctx.fillStyle = colour + '33'
        ctx.fill()
      }

      if (!s.noLine) {
        ctx.beginPath()
        s.points.forEach(([x, y], k) => {
          const px = sx(x)
          const py = sy(y)
          if (k === 0) ctx.moveTo(px, py)
          else ctx.lineTo(px, py)
        })
        ctx.strokeStyle = colour
        ctx.lineWidth = 2
        ctx.lineJoin = 'round'
        ctx.lineCap = 'round'
        ctx.setLineDash(s.dashed ? [5, 4] : [])
        ctx.stroke()
        ctx.setLineDash([])
      }
      if (s.markers) {
        ctx.fillStyle = colour
        for (const [x, y] of s.points) {
          ctx.beginPath()
          ctx.arc(sx(x), sy(y), 4, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    })

    // --- legend: always present for two or more series ---
    if (opts.series.length >= 2) {
      let lx = plot.x
      ctx.font = `10px ${FONT_STACK}`
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      opts.series.forEach((s, i) => {
        // An unlabelled series continues the one before it (the upper edge of
        // a band whose lower edge carries the name) and gets no swatch.
        if (!s.label) return
        const colour = s.colour ?? SERIES[i % SERIES.length]
        ctx.fillStyle = colour
        ctx.fillRect(lx, PAD.top - 14, 10, 3)
        ctx.fillStyle = INK.secondary
        ctx.fillText(s.label, lx + 14, PAD.top - 12)
        lx += 20 + ctx.measureText(s.label).width
      })
    } else if (opts.title) {
      ctx.font = `11px ${FONT_STACK}`
      ctx.fillStyle = INK.secondary
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      ctx.fillText(opts.title, plot.x, PAD.top - 12)
    }

    // --- axis labels ---
    ctx.fillStyle = INK.muted
    ctx.font = `10px ${FONT_STACK}`
    if (opts.xLabel) {
      ctx.textAlign = 'right'
      ctx.textBaseline = 'bottom'
      ctx.fillText(opts.xLabel, plot.x + plot.w, H - 2)
    }
    if (opts.yLabel) {
      ctx.save()
      ctx.translate(11, plot.y + plot.h / 2)
      ctx.rotate(-Math.PI / 2)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(opts.yLabel, 0, 0)
      ctx.restore()
    }

    // --- hover crosshair and readout ---
    if (this.hover && this.hover.x >= plot.x && this.hover.x <= plot.x + plot.w) {
      const xValue = xMin + ((this.hover.x - plot.x) / plot.w) * (xMax - xMin)
      ctx.strokeStyle = 'rgba(255,255,255,0.28)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(Math.round(this.hover.x) + 0.5, plot.y)
      ctx.lineTo(Math.round(this.hover.x) + 0.5, plot.y + plot.h)
      ctx.stroke()

      const readouts: string[] = []
      opts.series.forEach((s, i) => {
        if (s.points.length === 0) return
        // Nearest sample by x.
        let best = s.points[0]
        let bestD = Infinity
        for (const p of s.points) {
          const d = Math.abs(p[0] - xValue)
          if (d < bestD) {
            bestD = d
            best = p
          }
        }
        const colour = s.colour ?? SERIES[i % SERIES.length]
        ctx.fillStyle = colour
        ctx.beginPath()
        ctx.arc(sx(best[0]), sy(best[1]), 4, 0, Math.PI * 2)
        ctx.fill()
        ctx.strokeStyle = SURFACE
        ctx.lineWidth = 2
        ctx.stroke()
        readouts.push(
          opts.format
            ? opts.format(best[0], best[1])
            : `${s.label}: ${best[1] >= 100 ? best[1].toFixed(0) : best[1].toFixed(2)}`,
        )
      })

      if (readouts.length) {
        ctx.font = `10px ${FONT_STACK}`
        const wBox = Math.max(...readouts.map((t) => ctx.measureText(t).width)) + 12
        const hBox = readouts.length * 13 + 8
        const bx = clamp(this.hover.x + 10, plot.x, plot.x + plot.w - wBox)
        const by = plot.y + 4
        ctx.fillStyle = 'rgba(10,14,19,0.92)'
        ctx.fillRect(bx, by, wBox, hBox)
        ctx.strokeStyle = INK.border
        ctx.strokeRect(bx + 0.5, by + 0.5, wBox - 1, hBox - 1)
        ctx.fillStyle = INK.primary
        ctx.textAlign = 'left'
        ctx.textBaseline = 'top'
        readouts.forEach((t, i) => ctx.fillText(t, bx + 6, by + 5 + i * 13))
      }
    }

    return this.canvas
  }
}

/**
 * Size a one-shot canvas for crisp rendering: the backing store follows the
 * device pixel ratio while the element keeps its logical CSS size, and the
 * returned context is pre-scaled so all drawing stays in logical coordinates.
 */
export function crispContext(
  canvas: HTMLCanvasElement,
  w: number,
  h: number,
): CanvasRenderingContext2D {
  const dpr = Math.min(window.devicePixelRatio || 1, 3)
  canvas.width = Math.round(w * dpr)
  canvas.height = Math.round(h * dpr)
  canvas.style.width = w + 'px'
  canvas.style.maxWidth = '100%'
  const ctx = canvas.getContext('2d')!
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  return ctx
}
